import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView, useSpring } from 'framer-motion';

// Red Signal Brand Asset
const RED_SIGNAL = 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/r9thbfsf_showyn_red_icon_transparent.png';

// =====================================================
// 1. HERO TYPOGRAPHY REVEAL - Letter by letter animation
// =====================================================
export const HeroTypographyReveal = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const text = "Ideas are signals";
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 3500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <motion.div 
      className="fixed inset-0 z-[60] bg-white flex items-center justify-center"
      initial={{ opacity: 1 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="font-heading text-4xl md:text-6xl lg:text-7xl tracking-wide">
        {text.split('').map((char, index) => (
          <motion.span
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.1, 
              delay: index * 0.08,
              ease: "easeOut"
            }}
            className={char === ' ' ? 'inline-block w-4' : 'inline-block'}
          >
            {char}
          </motion.span>
        ))}
        <motion.img
          src={RED_SIGNAL}
          alt=""
          className="inline-block h-8 md:h-12 ml-2"
          style={{ transform: 'translateY(-8px)' }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
        />
      </div>
    </motion.div>
  );
};

// =====================================================
// 2. SIGNATURE MOMENT - IDEA → SIGNAL → STRUCTURE → BUSINESS
// =====================================================
export const SignatureMoment = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const stages = [
    { word: "IDEA", next: "SIGNAL" },
    { word: "SIGNAL", next: "STRUCTURE" },
    { word: "STRUCTURE", next: "BUSINESS" },
  ];

  return (
    <section 
      ref={containerRef}
      className="min-h-[200vh] relative"
      data-testid="signature-moment"
    >
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {/* Micro grid background */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.3) 1px, transparent 1px)
            `,
            backgroundSize: '30px 30px'
          }}
        />

        <div className="text-center px-6 relative z-10">
          {/* Oversized background text */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: useTransform(scrollYProgress, [0, 0.5], [0.03, 0.08]) }}
          >
            <span className="font-heading text-[20vw] text-neutral-900 select-none">
              IDEA
            </span>
          </motion.div>

          {/* Main animated text */}
          <div className="relative">
            {/* Stage 1: IDEA with signal */}
            <motion.div
              style={{ 
                opacity: useTransform(scrollYProgress, [0, 0.15, 0.25], [1, 1, 0]),
                y: useTransform(scrollYProgress, [0.15, 0.25], [0, -50])
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-tight">
                IDEA
              </span>
              <motion.img
                src={RED_SIGNAL}
                alt=""
                className="h-12 md:h-16 lg:h-20 ml-4"
                style={{ 
                  opacity: useTransform(scrollYProgress, [0.05, 0.12], [0, 1]),
                  scale: useTransform(scrollYProgress, [0.05, 0.12], [0.5, 1])
                }}
              />
            </motion.div>

            {/* Stage 2: SIGNAL */}
            <motion.div
              style={{ 
                opacity: useTransform(scrollYProgress, [0.2, 0.3, 0.45, 0.55], [0, 1, 1, 0]),
                y: useTransform(scrollYProgress, [0.2, 0.3], [50, 0])
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-tight">
                SIGNAL
              </span>
              <motion.img
                src={RED_SIGNAL}
                alt=""
                className="h-12 md:h-16 lg:h-20 ml-4"
              />
            </motion.div>

            {/* Stage 3: STRUCTURE */}
            <motion.div
              style={{ 
                opacity: useTransform(scrollYProgress, [0.5, 0.6, 0.75, 0.85], [0, 1, 1, 0]),
                y: useTransform(scrollYProgress, [0.5, 0.6], [50, 0])
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-tight">
                STRUCTURE
              </span>
            </motion.div>

            {/* Stage 4: BUSINESS */}
            <motion.div
              style={{ 
                opacity: useTransform(scrollYProgress, [0.8, 0.9], [0, 1]),
                y: useTransform(scrollYProgress, [0.8, 0.9], [50, 0])
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-tight italic">
                BUSINESS
              </span>
              <motion.img
                src={RED_SIGNAL}
                alt=""
                className="h-12 md:h-16 lg:h-20 ml-4"
              />
            </motion.div>

            {/* Placeholder for spacing */}
            <div className="opacity-0 font-heading text-6xl md:text-8xl lg:text-9xl">
              STRUCTURE
            </div>
          </div>

          {/* Subtitle */}
          <motion.p
            style={{ opacity: useTransform(scrollYProgress, [0.85, 0.95], [0, 1]) }}
            className="mt-8 font-mono text-sm tracking-[0.3em] text-neutral-500 uppercase"
          >
            Business Idea Architecture™
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// =====================================================
// 3. CHAPTER MARKER Component
// =====================================================
export const ChapterMarker = ({ number, title, subtitle }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="relative py-24 px-6 md:px-12"
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.8 }}
    >
      {/* Oversized background number */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span className="font-heading text-[30vw] md:text-[25vw] text-neutral-100 select-none">
          {number}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex items-start gap-6">
          {/* Chapter number */}
          <span className="font-mono text-xs tracking-[0.2em] text-editorial-red">
            {number}
          </span>
          
          {/* Thin red line */}
          <motion.div 
            className="w-12 h-px bg-editorial-red mt-2"
            initial={{ width: 0 }}
            animate={isInView ? { width: 48 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          
          {/* Title */}
          <div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-wide uppercase">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-2 font-mono text-xs tracking-[0.15em] text-neutral-500 uppercase">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// =====================================================
// 4. ARCHITECTURAL RED LINE - Draws across sections
// =====================================================
export const ArchitecturalRedLine = () => {
  const { scrollYProgress } = useScroll();
  const pathLength = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <div className="fixed left-8 top-0 bottom-0 z-40 pointer-events-none hidden lg:block">
      <svg 
        className="w-1 h-full"
        viewBox="0 0 4 1000"
        preserveAspectRatio="none"
      >
        <motion.line
          x1="2"
          y1="0"
          x2="2"
          y2="1000"
          stroke="#D00000"
          strokeWidth="1"
          style={{ pathLength }}
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
};

// =====================================================
// 5. IDEA SCANNER - Horizontal scanning line
// =====================================================
export const IdeaScanner = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0vh', '100vh']);
  const opacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 0.8, 0.8, 0]);

  return (
    <motion.div
      className="fixed left-0 right-0 h-px bg-gradient-to-r from-transparent via-editorial-red to-transparent z-30 pointer-events-none"
      style={{ top: y, opacity }}
    />
  );
};

// =====================================================
// 6. RED SIGNAL with Pulse Micro-interaction
// =====================================================
export const RedSignalPulse = ({ size = 'md', className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const sizeClasses = {
    xs: 'h-3 w-auto',
    sm: 'h-5 w-auto',
    md: 'h-8 w-auto',
    lg: 'h-12 w-auto',
    xl: 'h-16 w-auto',
  };

  return (
    <motion.div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow effect */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={isHovered ? {
          boxShadow: ['0 0 0 0 rgba(208, 0, 0, 0)', '0 0 20px 5px rgba(208, 0, 0, 0.3)', '0 0 0 0 rgba(208, 0, 0, 0)'],
        } : {}}
        transition={{ duration: 1.5, repeat: isHovered ? Infinity : 0 }}
      />
      <motion.img 
        src={RED_SIGNAL}
        alt=""
        className={sizeClasses[size]}
        style={{ objectFit: 'contain' }}
        animate={isHovered ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 0.6, repeat: isHovered ? Infinity : 0 }}
      />
    </motion.div>
  );
};

// =====================================================
// 7. ARCHITECTURAL BLOCKS - Stacking animation (Fixed)
// =====================================================
const ArchitecturalBlockItem = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="border-l border-editorial-border pl-6 py-8"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      <span className="font-mono text-xs tracking-[0.2em] text-editorial-red mb-2 block">
        0{index + 1}
      </span>
      <h3 className="font-heading text-2xl md:text-3xl italic mb-2">
        {item.title}
      </h3>
      <p className="text-neutral-600 text-sm md:text-base max-w-md">
        {item.description}
      </p>
    </motion.div>
  );
};

export const ArchitecturalBlocks = ({ items }) => {
  return (
    <div className="relative">
      {items.map((item, index) => (
        <ArchitecturalBlockItem key={item.title} item={item} index={index} />
      ))}
    </div>
  );
};

// =====================================================
// 8. IDEA WALL CARD with Hover Effect
// =====================================================
export const IdeaWallCard = ({ src, alt, className = '', style = {} }) => {
  return (
    <motion.div
      className={`relative cursor-pointer ${className}`}
      style={style}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)'
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <img 
        src={src}
        alt={alt}
        className="w-full h-auto object-contain"
      />
    </motion.div>
  );
};

// =====================================================
// 9. OVERSIZED BACKGROUND TEXT
// =====================================================
export const OversizedText = ({ text, className = '' }) => {
  return (
    <div className={`absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden ${className}`}>
      <span className="font-heading text-[15vw] md:text-[12vw] text-neutral-100 select-none whitespace-nowrap">
        {text}
      </span>
    </div>
  );
};

// =====================================================
// 10. MICRO GRID BACKGROUND
// =====================================================
export const MicroGridBackground = ({ opacity = 0.02 }) => (
  <div 
    className="absolute inset-0 pointer-events-none"
    style={{
      opacity,
      backgroundImage: `
        linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)
      `,
      backgroundSize: '40px 40px'
    }}
  />
);

// =====================================================
// 11. EDITORIAL MICRO-PHRASE - Subtle intellectual notes
// =====================================================
export const EditorialMicroPhrase = ({ text, position = 'left', className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  const positionClasses = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  };

  return (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className={`font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-neutral-400 ${positionClasses[position]} ${className}`}
    >
      {text}
    </motion.p>
  );
};

// =====================================================
// 12. WATERMARK TEXT - Large low-opacity background phrase
// =====================================================
export const WatermarkText = ({ text, className = '' }) => {
  return (
    <div className={`absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden ${className}`}>
      <span 
        className="font-heading text-[8vw] md:text-[6vw] text-neutral-900 select-none whitespace-nowrap uppercase tracking-widest"
        style={{ opacity: 0.03 }}
      >
        {text}
      </span>
    </div>
  );
};

// =====================================================
// 13. RED SIGNAL SUPERSCRIPT - Positioned like apostrophe
// =====================================================
export const RedSignalSuperscript = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    xs: 'h-2 w-auto',
    sm: 'h-3 w-auto',
    md: 'h-4 md:h-5 w-auto',
    lg: 'h-6 md:h-8 w-auto',
    xl: 'h-8 md:h-10 w-auto',
  };

  return (
    <img 
      src={RED_SIGNAL}
      alt=""
      className={`inline-block align-top ml-0.5 ${sizeClasses[size]} ${className}`}
      style={{ objectFit: 'contain', verticalAlign: 'super', transform: 'translateY(-8px)' }}
    />
  );
};

// =====================================================
// 14. ARCHITECTURAL SPINE LAYOUT - BIA Method
// =====================================================
export const ArchitecturalSpine = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  
  // Steps from bottom to top, alternating sides
  const steps = [
    { num: '01', title: 'Idea Discovery', description: 'Identify signals, patterns and opportunities.', side: 'right' },
    { num: '02', title: 'Market Strategy', description: 'Define positioning, offer and growth path.', side: 'left' },
    { num: '03', title: 'Idea Validation', description: 'Test assumptions and confirm market relevance.', side: 'right' },
    { num: '04', title: 'Business Realization', description: 'Execute and launch with focus and clarity.', side: 'left' },
    { num: '05', title: 'Business Architecture', description: 'Design the structure that transforms the idea into a viable business.', side: 'center' },
  ];

  return (
    <div ref={containerRef} className="relative py-20">
      {/* Central Vertical Axis */}
      <motion.div
        className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-neutral-200 -translate-x-1/2"
        initial={{ scaleY: 0 }}
        animate={isInView ? { scaleY: 1 } : {}}
        transition={{ duration: 1.2, ease: "easeOut" }}
        style={{ transformOrigin: 'bottom' }}
      />

      {/* Steps */}
      <div className="relative flex flex-col-reverse gap-16 md:gap-24">
        {steps.map((step, index) => (
          <motion.div
            key={step.num}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 + index * 0.2 }}
            className={`relative ${
              step.side === 'center' 
                ? 'text-center mx-auto max-w-md' 
                : step.side === 'left' 
                  ? 'md:mr-[55%] md:text-right md:pr-16' 
                  : 'md:ml-[55%] md:text-left md:pl-16'
            }`}
          >
            {/* Connection Node - Red Signal Symbol */}
            {step.side !== 'center' && (
              <motion.div
                className={`hidden md:block absolute top-4 ${
                  step.side === 'left' ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
                }`}
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.2 }}
              >
                <img src={RED_SIGNAL} alt="" className="h-3 w-auto" />
              </motion.div>
            )}

            {/* Horizontal Connector Line */}
            {step.side !== 'center' && (
              <motion.div
                className={`hidden md:block absolute top-5 h-px bg-neutral-300 ${
                  step.side === 'left' ? 'right-0 w-12' : 'left-0 w-12'
                }`}
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.2 }}
                style={{ transformOrigin: step.side === 'left' ? 'right' : 'left' }}
              />
            )}

            {/* Top Node for Center (Final step) */}
            {step.side === 'center' && (
              <motion.div
                className="absolute left-1/2 -translate-x-1/2 -bottom-8"
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.2 }}
              >
                <img src={RED_SIGNAL} alt="" className="h-4 w-auto" />
              </motion.div>
            )}

            {/* Content */}
            <div className={step.side === 'center' ? 'bg-white relative z-10 px-8 py-6 border border-editorial-red' : ''}>
              <span className="font-mono text-xs tracking-[0.2em] text-editorial-red block mb-2">
                {step.num}
              </span>
              <h3 className={`font-heading ${step.side === 'center' ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'} italic mb-2`}>
                {step.title}
              </h3>
              <p className="text-neutral-600 text-sm md:text-base max-w-xs mx-auto md:mx-0">
                {step.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default {
  HeroTypographyReveal,
  SignatureMoment,
  ChapterMarker,
  ArchitecturalRedLine,
  IdeaScanner,
  RedSignalPulse,
  ArchitecturalBlocks,
  IdeaWallCard,
  OversizedText,
  MicroGridBackground,
  EditorialMicroPhrase,
  WatermarkText,
  RedSignalSuperscript,
  ArchitecturalSpine
};
